import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, ShieldCheck } from "lucide-react";
import { useCart } from "@/hooks/useCart";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { formatPrice } from "@/data/products";
import { cn } from "@/lib/utils";

interface CartDrawerProps {
  className?: string;
}

export function CartDrawer({ className }: CartDrawerProps) {
  const { items, itemCount, subtotal, updateQuantity, removeFromCart, loading } = useCart();

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button 
          className={cn(
            "relative p-2.5 transition-all duration-300 group",
            "hover:text-gold hover:scale-110",
            className
          )} 
          aria-label="Shopping Bag"
        >
          {/* Elegant bag icon with thin strokes */}
          <svg 
            width="22" 
            height="22" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="1.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className="transition-transform duration-300 group-hover:scale-105"
          >
            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 01-8 0" />
          </svg>
          
          {/* Premium badge with subtle animation */}
          {itemCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-gold text-white text-[9px] font-bold rounded-full flex items-center justify-center animate-scale-in shadow-sm">
              {itemCount > 99 ? "99+" : itemCount}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="flex flex-col w-full sm:max-w-md bg-white border-l border-gold/10 p-0">
        <SheetHeader className="p-8 border-b border-gold/5 bg-ivory/20">
          <SheetTitle className="font-display text-2xl tracking-tight text-charcoal flex items-center justify-between">
            Your Selection
            <span className="text-xs font-body uppercase tracking-[0.2em] text-muted-foreground mr-8">({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-12">
            <div className="w-20 h-20 bg-ivory rounded-full flex items-center justify-center mb-6 text-muted-foreground/30">
              <ShoppingBag size={32} strokeWidth={1} />
            </div>
            <h3 className="font-display text-2xl mb-2 text-charcoal uppercase tracking-widest">Awaiting Treasures</h3>
            <p className="text-muted-foreground mb-10 max-w-[240px] font-light text-sm leading-relaxed">
              Your bag is currently empty. Explore our latest heritage collections to find your perfect piece.
            </p>
            <SheetTrigger asChild>
              <Link to="/shop">
                <Button className="px-10 h-14 bg-charcoal text-white uppercase tracking-[0.2em]">Explore Now</Button>
              </Link>
            </SheetTrigger>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 px-8">
              <div className="divide-y divide-gold/5">
                {items.map((item) => (
                  <div key={item.id} className="flex gap-6 py-8 animate-fade-in group">
                    <Link
                      to={`/product/${item.product?.slug}`}
                      className="w-24 h-32 bg-ivory rounded-sm overflow-hidden flex-shrink-0 border border-gold/5 shadow-sm"
                    >
                      <img
                        src={item.product?.images?.[0] || "/placeholder.svg"}
                        alt={item.product?.name}
                        className="w-full h-full object-contain bg-ivory transition-transform duration-700 group-hover:scale-105"
                      />
                    </Link>
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="flex justify-between items-start">
                         <div className="flex-1 pr-2">
                            <Link
                              to={`/product/${item.product?.slug}`}
                              className="font-display text-base text-charcoal hover:text-gold transition-colors block leading-tight mb-1"
                            >
                              {item.product?.name}
                            </Link>
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                               {(item.product as any)?.category?.name || "Collection"}
                            </p>
                         </div>
                         <p className="font-medium text-sm text-charcoal">
                           {formatPrice(item.product?.price || 0)}
                         </p>
                      </div>

                      <div className="mt-auto flex items-center justify-between pt-4">
                        <div className="flex items-center bg-gray-50 rounded-sm">
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.variant_id, item.id)}
                            className="p-2 hover:text-gold transition-colors disabled:opacity-30"
                            disabled={loading || item.quantity <= 1}
                            aria-label="Decrease quantity"
                          >
                            <Minus size={12} />
                          </button>
                          <span className="px-3 text-xs font-bold text-charcoal min-w-[2rem] text-center">
                            {item.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.variant_id, item.id)}
                            className="p-2 hover:text-gold transition-colors"
                            disabled={loading}
                            aria-label="Increase quantity"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFromCart(item.product_id, item.variant_id, item.id)}
                          className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:text-red-500 transition-colors"
                          disabled={loading}
                        >
                          <Trash2 size={12} />
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="p-8 space-y-6 bg-ivory/10 border-t border-gold/10">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Total Selection</span>
                  <span className="font-display text-2xl text-charcoal">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground uppercase tracking-[0.2em] py-2 px-3 bg-white border border-gold/10 rounded-sm">
                   <ShieldCheck size={14} className="text-emerald-600" />
                   Fully Insured & Authenticated Delivery
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <SheetTrigger asChild>
                  <Link to="/cart">
                    <Button variant="outline" className="w-full h-14 border-gold/20 text-charcoal uppercase tracking-[0.2em] text-[10px] hover:bg-ivory hover:text-charcoal transition-all">
                      View Bag
                    </Button>
                  </Link>
                </SheetTrigger>
                <SheetTrigger asChild>
                  <Link to="/checkout">
                    <Button className="w-full h-14 bg-charcoal text-white shadow-gold uppercase tracking-[0.2em] text-[10px] hover:bg-gold transition-all">
                      Checkout
                    </Button>
                  </Link>
                </SheetTrigger>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
